// =============================================================================
// MODULE AI PROMPT - Inline AI command interface for each module
// =============================================================================

import { useState, useRef, useEffect } from 'react';
import { Sparkles, Send, Loader2, X, Lightbulb, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { cn } from '../lib/utils';

export type ModuleType = 'form' | 'workflow' | 'data' | 'app';

interface Suggestion {
  text: string;
  description: string;
}

const MODULE_SUGGESTIONS: Record<ModuleType, Suggestion[]> = {
  form: [
    { text: 'Créer un formulaire de contact avec nom, email et message', description: 'Formulaire basique' },
    { text: 'Ajouter un champ téléphone obligatoire', description: 'Ajouter un champ' },
    { text: 'Créer une section "Informations personnelles" avec 5 champs', description: 'Nouvelle section' },
    { text: 'Ajouter une validation email sur le champ email', description: 'Validation' },
    { text: 'Transformer ce formulaire en enquête de satisfaction', description: 'Transformation' },
  ],
  workflow: [
    { text: 'Créer un workflow d\'approbation à 3 étapes', description: 'Workflow d\'approbation' },
    { text: 'Ajouter une condition si le montant est > 1000€', description: 'Condition' },
    { text: 'Envoyer un email de notification à la fin', description: 'Action email' },
    { text: 'Ajouter une boucle pour traiter tous les éléments', description: 'Boucle' },
    { text: 'Créer un workflow de traitement de commande', description: 'E-commerce' },
  ],
  data: [
    { text: 'Créer une entité Client avec nom, email, téléphone', description: 'Nouvelle entité' },
    { text: 'Ajouter une relation one-to-many entre Client et Commande', description: 'Relation' },
    { text: 'Créer un modèle e-commerce complet', description: 'Modèle complet' },
    { text: 'Ajouter un champ statut à l\'entité Commande', description: 'Nouveau champ' },
    { text: 'Créer une entité Produit avec catégorie, prix, stock', description: 'Entité produit' },
  ],
  app: [
    { text: 'Configurer les paramètres de sécurité', description: 'Sécurité' },
    { text: 'Ajouter une page d\'accueil personnalisée', description: 'Page' },
    { text: 'Configurer les notifications email', description: 'Notifications' },
    { text: 'Activer l\'authentification à deux facteurs', description: '2FA' },
    { text: 'Définir les rôles et permissions', description: 'Permissions' },
  ],
};

const MODULE_LABELS: Record<ModuleType, { title: string; placeholder: string }> = {
  form: {
    title: 'Assistant Formulaire',
    placeholder: 'Ex: "Crée un formulaire de contact avec nom, email et message"',
  },
  workflow: {
    title: 'Assistant Workflow',
    placeholder: 'Ex: "Crée un workflow d\'approbation à 3 étapes"',
  },
  data: {
    title: 'Assistant Modèle',
    placeholder: 'Ex: "Crée une entité Client avec nom, email, téléphone"',
  },
  app: {
    title: 'Assistant Application',
    placeholder: 'Ex: "Configure les paramètres de sécurité"',
  },
};

interface ModuleAIPromptProps {
  moduleType: ModuleType;
  onExecute: (prompt: string) => Promise<void>;
  isProcessing?: boolean;
  className?: string;
  compact?: boolean;
}

export function ModuleAIPrompt({
  moduleType,
  onExecute,
  isProcessing = false,
  className,
  compact = false,
}: ModuleAIPromptProps) {
  const [isExpanded, setIsExpanded] = useState(!compact);
  const [prompt, setPrompt] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [lastResponse, setLastResponse] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const suggestions = MODULE_SUGGESTIONS[moduleType];
  const labels = MODULE_LABELS[moduleType];

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!prompt.trim() || isProcessing) return;

    try {
      setLastResponse(null);
      await onExecute(prompt.trim());
      setLastResponse(`Demande exécutée: "${prompt.trim()}"`);
      setPrompt('');
    } catch (error) {
      setLastResponse(`Erreur: ${error instanceof Error ? error.message : 'Échec de l\'exécution'}`);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleSuggestionClick = (suggestion: Suggestion) => {
    setPrompt(suggestion.text);
    setShowSuggestions(false);
    textareaRef.current?.focus();
  };

  useEffect(() => {
    if (isExpanded && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isExpanded]);

  // Compact toggle button
  if (compact && !isExpanded) {
    return (
      <button
        onClick={() => setIsExpanded(true)}
        className={cn(
          'flex items-center gap-2 px-4 py-2 rounded-lg',
          'bg-gradient-to-r from-purple-500/10 to-blue-500/10',
          'border border-purple-200 dark:border-purple-800',
          'hover:from-purple-500/20 hover:to-blue-500/20',
          'transition-all duration-200',
          className
        )}
      >
        <Sparkles className="h-4 w-4 text-purple-500" />
        <span className="text-sm font-medium text-purple-700 dark:text-purple-300">
          {labels.title}
        </span>
      </button>
    );
  }

  return (
    <div
      className={cn(
        'rounded-xl border overflow-hidden',
        'bg-gradient-to-br from-purple-50/50 to-blue-50/50',
        'dark:from-purple-900/10 dark:to-blue-900/10',
        'border-purple-200/50 dark:border-purple-800/50',
        className
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-white/50 dark:bg-secondary-900/50 border-b border-purple-100 dark:border-purple-800/30">
        <div className="flex items-center gap-2">
          <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-blue-500">
            <Sparkles className="h-4 w-4 text-white" />
          </div>
          <div>
            <h4 className="font-semibold text-secondary-900 dark:text-white text-sm">
              {labels.title}
            </h4>
            <p className="text-[10px] text-secondary-500">
              Décrivez ce que vous voulez créer
            </p>
          </div>
        </div>
        {compact && (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => setIsExpanded(false)}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Main input area */}
      <div className="p-4">
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="relative">
            <Textarea
              ref={textareaRef}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={labels.placeholder}
              rows={2}
              disabled={isProcessing}
              className={cn(
                'resize-none pr-12',
                'bg-white dark:bg-secondary-800',
                'border-purple-200 dark:border-purple-700',
                'focus:border-purple-400 focus:ring-purple-400/20'
              )}
            />
            <Button
              type="submit"
              size="icon"
              disabled={!prompt.trim() || isProcessing}
              className={cn(
                'absolute right-2 bottom-2 h-8 w-8',
                'bg-gradient-to-r from-purple-500 to-blue-500',
                'hover:from-purple-600 hover:to-blue-600',
                'disabled:opacity-50'
              )}
            >
              {isProcessing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </div>

          {/* Response message */}
          {lastResponse && (
            <div className={cn(
              'text-xs px-3 py-2 rounded-lg',
              lastResponse.startsWith('Erreur')
                ? 'bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400'
                : 'bg-green-50 text-green-600 dark:bg-green-900/20 dark:text-green-400'
            )}>
              {lastResponse}
            </div>
          )}
        </form>

        {/* Suggestions toggle */}
        <button
          type="button"
          onClick={() => setShowSuggestions(!showSuggestions)}
          className="flex items-center gap-1 mt-3 text-xs text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 transition-colors"
        >
          <Lightbulb className="h-3 w-3" />
          <span>Suggestions</span>
          {showSuggestions ? (
            <ChevronUp className="h-3 w-3" />
          ) : (
            <ChevronDown className="h-3 w-3" />
          )}
        </button>

        {/* Suggestions list */}
        {showSuggestions && (
          <div className="mt-2 space-y-1">
            {suggestions.map((suggestion, index) => (
              <button
                key={index}
                type="button"
                onClick={() => handleSuggestionClick(suggestion)}
                className={cn(
                  'w-full text-left px-3 py-2 rounded-lg text-xs',
                  'bg-white/70 dark:bg-secondary-800/70',
                  'border border-purple-100 dark:border-purple-800/30',
                  'hover:bg-purple-50 dark:hover:bg-purple-900/20',
                  'hover:border-purple-200 dark:hover:border-purple-700',
                  'transition-all duration-150'
                )}
              >
                <span className="font-medium text-secondary-800 dark:text-secondary-200">
                  {suggestion.text}
                </span>
                <span className="ml-2 text-secondary-400">
                  — {suggestion.description}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default ModuleAIPrompt;
